import { Cell, Pie, PieChart } from 'recharts';
import { formatNumbersForCharts } from 'shared/utils/common.utils';

type GaugeProps = {
  min?: number;
  max: number;
  value: number;
  target?: number;
  color?: string;
  unit?: '€' | '%';
  title: string;
};

const GaugeChart: React.FC<GaugeProps> = ({ min = 0, max, value, target, color = '#1b7012', unit = '€', title }) => {
  const cx = 200;
  const cy = 170;
  const iR = 75;
  const oR = 110;

  const total = max - min;
  const filled = Math.max(0, Math.min(total, value - min));

  const chartData = [
    { name: 'filled', value: filled, color },
    { name: 'empty', value: total - filled, color: '#f2f2f2' },
  ];

  const drawTarget = (targetVal: number) => {
    const angle = 180 * (1 - (targetVal - min) / total);
    const RADIAN = Math.PI / 180;
    const cos = Math.cos(-RADIAN * angle);
    const sin = Math.sin(-RADIAN * angle);

    const rOuter = oR; // raggio esterno
    const rInner = iR; // raggio interno

    // punti del target
    const x1 = cx + rOuter * cos;
    const y1 = cy + rOuter * sin;
    const x2 = cx + rInner * cos;
    const y2 = cy + rInner * sin;

    const labelR = rOuter + 10;
    const labelX = cx + labelR * cos;
    const labelY = cy + labelR * sin;

    return (
      <>
        {/* segmento target */}
        <line x1={x1 + 5} y1={y1 + 5} x2={x2 + 5} y2={y2 + 5} stroke="limegreen" strokeWidth={4} strokeLinecap="butt" />

        {/* testo target */}
        <text
          x={labelX}
          y={labelY}
          textAnchor={cos >= 0 ? 'start' : 'end'}
          dominantBaseline="middle"
          fill="#666"
          fontSize={14}
        >
          {formatNumbersForCharts(targetVal, unit)}
        </text>
      </>
    );
  };

  return (
    <>
      <PieChart width={400} height={220}>
        <Pie
          data={chartData}
          cx={cx}
          cy={cy}
          startAngle={180}
          endAngle={0}
          innerRadius={iR}
          outerRadius={oR}
          paddingAngle={0}
          dataKey="value"
          stroke="none"
        >
          {chartData.map((entry) => (
            <Cell key={`cell-${entry.name}`} fill={entry.color} />
          ))}
        </Pie>

        {/* stanghetta target */}
        {target && drawTarget(target)}

        {/* titolo */}
        <text x={200} y={10} textAnchor="middle" dominantBaseline="middle" fontSize={16} fontWeight={600} fill={color}>
          {title}
        </text>

        {/* valore centrale */}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle" fontSize={20} fill="#333">
          {formatNumbersForCharts(value, unit)}
        </text>

        {/* etichette min e max */}
        <text x={cx - 105} y={cy + 30} textAnchor="start" fill="#666">
          {formatNumbersForCharts(min, unit)}
        </text>
        <text x={cx + 115} y={cy + 30} textAnchor="end" fill="#666">
          {formatNumbersForCharts(max, unit)}
        </text>
      </PieChart>
    </>
  );
};

export default GaugeChart;
